// CommitHeader — metadata header for a single commit.
// Props: { commit: CommitDetail }

import { useState } from "react";
import { Link } from "react-router-dom";
import type { CommitDetail } from "../diff/useCommit.js";
import { repoCommit } from "../lib/paths";
import { useTranslation } from "../i18n";
import { useContextMenu, type ContextMenuItem } from "../ui/ContextMenu";

export interface CommitHeaderProps {
  readonly commit: CommitDetail;
  /** e.g. "alice" — needed to build parent links */
  readonly slug: string;
  readonly repo: string;
}

function formatDate(isoString: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(new Date(isoString));
  } catch {
    return isoString;
  }
}

export function CommitHeader({ commit, slug, repo }: CommitHeaderProps) {
  const { t } = useTranslation("history");
  const [copied, setCopied] = useState(false);
  const menu = useContextMenu();
  const shortOid = commit.oid.slice(0, 7);
  const oidMenuItems: ContextMenuItem[] = [
    { label: t("menu.copySha"), onClick: () => void navigator.clipboard?.writeText(commit.oid) },
    { label: t("menu.copyShortSha"), onClick: () => void navigator.clipboard?.writeText(shortOid) },
  ];
  const [firstLine, ...rest] = commit.message.split("\n");
  const body = rest.join("\n").trim();

  function handleCopy() {
    navigator.clipboard.writeText(commit.oid).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div
      className="rounded-xl border border-slate-800 bg-slate-900 p-5"
      data-testid="commit-header"
    >
      {/* OID + copy */}
      <div className="mb-3 flex items-center gap-2">
        <code
          className="rounded bg-slate-800 px-2 py-0.5 font-mono text-xs text-slate-300"
          title={commit.oid}
          onContextMenu={(e) => menu.open(e, oidMenuItems)}
        >
          {shortOid}
        </code>
        <button
          type="button"
          className="rounded-md px-2 py-0.5 text-xs text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
          onClick={handleCopy}
          aria-label={t("header.copyHash")}
        >
          {copied ? t("header.copied") : t("header.copy")}
        </button>
      </div>

      {/* Message */}
      <div className="mb-3">
        <strong className="block text-base font-semibold text-slate-100">{firstLine}</strong>
        {body && (
          <pre className="mt-2 whitespace-pre-wrap font-mono text-sm leading-relaxed text-slate-400">{body}</pre>
        )}
      </div>

      {/* Author + date */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
        <span className="font-medium text-slate-300">{commit.author}</span>
        <span aria-hidden="true">·</span>
        <time dateTime={commit.timestamp}>{formatDate(commit.timestamp)}</time>
      </div>

      {/* Parents */}
      {commit.parentOids.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-800 pt-3 text-xs text-slate-500">
          <span>{commit.parentOids.length === 1 ? t("header.parent") : t("header.parents")}</span>
          {commit.parentOids.map((p) => (
            <Link
              key={p}
              to={repoCommit(slug, repo, p)}
              className="font-mono text-arch-400 hover:text-arch-300 hover:underline"
            >
              {p.slice(0, 7)}
            </Link>
          ))}
        </div>
      )}
      {menu.node}
    </div>
  );
}
